/**
 * PageHelp — collapsible help banner shown at the top of admin pages.
 *
 * Usage:
 *   <PageHelp
 *     title="So funktioniert die Seite"
 *     storageKey="social-accounts-help"
 *     defaultOpen
 *   >
 *     <p>...erste Erklärung...</p>
 *     <h4>Wann brauche ich das?</h4>
 *     <ol>...</ol>
 *   </PageHelp>
 *
 * State is persisted in localStorage per storageKey, so a closed help stays
 * closed for that user on that browser.
 */
import { useState, useEffect } from "react";
import { ChevronDown, ChevronUp, HelpCircle } from "lucide-react";

export default function PageHelp({
  title = "So funktioniert diese Seite",
  storageKey,
  defaultOpen = false,
  children,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const lsKey = storageKey ? `pageHelp:${storageKey}` : null;

  useEffect(() => {
    if (!lsKey) return;
    const saved = localStorage.getItem(lsKey);
    if (saved === "closed") setOpen(false);
    if (saved === "open") setOpen(true);
  }, [lsKey]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (lsKey) localStorage.setItem(lsKey, next ? "open" : "closed");
  };

  return (
    <div
      className="rounded-xl border border-blue-200 bg-blue-50/60 mb-6"
      data-testid={storageKey ? `page-help-${storageKey}` : "page-help"}
    >
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        data-testid="page-help-toggle"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-blue-900">
          <HelpCircle className="w-4 h-4" />
          {title}
        </span>
        {open ? (
          <ChevronUp className="w-4 h-4 text-blue-900" />
        ) : (
          <ChevronDown className="w-4 h-4 text-blue-900" />
        )}
      </button>
      {open && (
        <div
          className="px-4 pb-4 text-sm text-blue-950 space-y-2 leading-relaxed [&_h4]:font-semibold [&_h4]:mt-3 [&_h4]:text-blue-900 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_code]:bg-blue-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-[12px] [&_code]:font-mono [&_a]:text-blue-700 [&_a]:underline"
          data-testid="page-help-body"
        >
          {children}
        </div>
      )}
    </div>
  );
}
